/**
 * useSearchAccounts Hook
 *
 * Universal account search hook for admin features.
 * Includes telegramId field needed for ticket issuance.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const SearchAccountsDocument = `
  query SearchAccounts($query: String!, $limit: Int) {
    searchAccounts(query: $query, limit: $limit) {
      id
      firstName
      lastName
      username
      telegramId
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface SearchAccountResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string | null;
}

interface UseSearchAccountsOptions {
  query: string;
  limit?: number;
  /** Minimum query length to trigger search */
  minLength?: number;
  pause?: boolean;
}

interface UseSearchAccountsResult {
  accounts: SearchAccountResult[];
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Search accounts by query string.
 * Searches by name, username, email, phone, telegram ID.
 *
 * @param options.query - Search query (min 2 chars by default)
 * @param options.limit - Max results (default 20)
 * @param options.minLength - Min query length to trigger search (default 2)
 */
export function useSearchAccounts(
  options: UseSearchAccountsOptions
): UseSearchAccountsResult {
  const { query, limit = 20, minLength = 2, pause = false } = options;

  const shouldSearch = query.length >= minLength;

  const [result] = useQuery<{ searchAccounts: SearchAccountResult[] }>({
    query: SearchAccountsDocument,
    variables: { query, limit },
    pause: pause || !shouldSearch,
  });

  return {
    accounts: shouldSearch ? (result.data?.searchAccounts ?? []) : [],
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

// =============================================================================
// ADMIN ACCOUNTS QUERY (all admins)
// =============================================================================

const AdminAccountsDocument = `
  query AdminAccounts {
    adminAccounts {
      id
      firstName
      lastName
      username
      telegramId
    }
  }
`;

interface UseAdminAccountsOptions {
  pause?: boolean;
}

interface UseAdminAccountsResult {
  accounts: SearchAccountResult[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetch all admin accounts (operators + admins).
 * Useful for initial lists before search.
 */
export function useAdminAccounts(
  options: UseAdminAccountsOptions = {}
): UseAdminAccountsResult {
  const { pause = false } = options;

  const [result] = useQuery<{ adminAccounts: SearchAccountResult[] }>({
    query: AdminAccountsDocument,
    pause,
  });

  return {
    accounts: result.data?.adminAccounts ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
